import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Chip,
  Divider,
  Stack,
  Typography,
} from "@mui/joy";
import { Send } from "lucide-react";
import { api } from "../../lib/api";
import type { Contract } from "../../types";
import {
  type Deal,
  type DealDocument,
  dealError,
  proposalDealsApi,
} from "../../lib/proposalDealsApi";
import { HelpTooltip } from "../../components/cms/HelpTooltip";
import {
  SendContractModal,
  type SendContractData,
} from "../../components/crm/contracts/SendContractModal";
import { PdfViewerModal } from "../../components/crm/contracts/PdfViewerModal";
import {
  invitationResults,
  invitationSummary,
} from "../../components/crm/contracts/contractWorkflow";
import { ProposalJobHandoff } from "./ProposalJobHandoff";
import { ManualRepairAuthorization } from "./ManualRepairAuthorization";

export function RepairNextStep({
  deal,
  pending,
  prepare,
  refresh,
  onTab,
}: {
  deal: Deal;
  pending: boolean;
  prepare: () => Promise<DealDocument>;
  refresh: () => Promise<void>;
  onTab: (tab: string) => void;
}) {
  const [contract, setContract] = useState<Contract | null>(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const doc = deal.documents.find((d) => d.kind === "repair_authorization");
  const manual = deal.documents.find(
    (d) => d.kind === "manual_authorization" && d.state === "ready",
  );
  const status = doc?.contract_status;
  const sent = ["sent", "viewed", "in_progress"].includes(status || "");
  const openSend = async () => {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const prepared = !doc
        ? await prepare()
        : ["pending", "error"].includes(doc.state)
          ? await proposalDealsApi.action<DealDocument>(
              deal.id,
              `documents/${doc.id}/retry`,
            )
          : doc;
      if (prepared.state !== "ready" || !prepared.contract_id)
        throw new Error(
          prepared.error ||
            "The authorization is not ready yet. Try again to continue with the same saved document.",
        );
      if (prepared.contract_status !== "draft")
        throw new Error(
          "This authorization has changed. Check the updated signing status before continuing.",
        );
      const current = (
        await api.get<Contract>(`/api/crm/contracts/${prepared.contract_id}/`)
      ).data;
      if (current.status !== "draft")
        throw new Error(
          "This authorization has changed. Check the updated signing status before continuing.",
        );
      setContract(current);
    } catch (e) {
      setError(dealError(e));
    } finally {
      setBusy(false);
      await refresh();
    }
  };
  const send = async (data: SendContractData) => {
    try {
      const response = await api.post(
        `/api/crm/contracts/${contract!.id}/send/`,
        data,
      );
      const rows = invitationResults(response.data);
      setMessage(
        rows.length
          ? invitationSummary(rows)
          : "Check signing status to confirm invitation delivery.",
      );
      setContract(null);
    } catch (e) {
      setContract(null);
      setError(
        `${dealError(e)} Check signing status before retrying the invitation.`,
      );
    } finally {
      await refresh();
    }
  };
  if (status === "completed" || manual)
    return (
      <Stack spacing={2}>
        {manual?.authorization && (
          <Alert color="success">
            <div>
              <Typography level="title-md">
                Authorized by {manual.authorization.recorded_by_name}
              </Typography>
              <Typography level="body-sm">
                {manual.authorization.reason}
              </Typography>
            </div>
          </Alert>
        )}
        <ProposalJobHandoff
          endpoint={`/api/proposal-deals/${deal.id}/job-handoff/`}
          authorizedByStaff={!!manual}
        />
      </Stack>
    );
  const canReview =
    status === "draft" ||
    (!doc && deal.readiness.ready) ||
    (!!doc && ["pending", "error"].includes(doc.state));
  const title = sent
    ? "Waiting for the homeowner’s signature"
    : busy
      ? "Getting your proposal ready…"
      : canReview
        ? "Send your repair proposal"
        : doc
          ? "Authorization needs attention"
          : deal.readiness.ready
            ? "Your repair scope is ready"
            : "Finish your repair proposal";
  return (
    <Card
      variant="outlined"
      sx={{ p: { xs: 2, md: 3 }, borderColor: "primary.outlinedBorder" }}
    >
      <Stack direction="row" gap={1} flexWrap="wrap">
        <Chip size="sm">1. Scope & price</Chip>
        <Chip
          size="sm"
          color="primary"
          variant={canReview || sent ? "solid" : "soft"}
        >
          2. Send & sign
        </Chip>
        <Chip size="sm">3. Admin confirms job</Chip>
      </Stack>
      <Typography level="h3" component="h2">
        {title}
      </Typography>
      <Typography>
        {sent
          ? "After signing, Org Admins receive a notification to mark the opportunity Won and create its linked job."
          : "Review the recipient and message, then send the customer a link to review and sign the repair scope, price, and terms."}
      </Typography>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        gap={2}
        sx={{ p: 2, borderRadius: "sm", bgcolor: "background.level1" }}
      >
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Typography level="body-sm">Customer</Typography>
          <Typography level="title-md">{deal.contact_name}</Typography>
          <Typography level="body-sm" sx={{ overflowWrap: "anywhere" }}>
            {deal.contact_email || "Add the customer’s email before sending."}
          </Typography>
        </Stack>
        <Stack>
          <Typography level="body-sm">Repair total</Typography>
          <Typography level="h3">
            {new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(Number(deal.totals?.total || 0))}
          </Typography>
        </Stack>
      </Stack>
      {doc && ["pending", "error"].includes(doc.state) && !busy && (
        <Alert color="warning">
          {doc.state === "error"
            ? "The authorization could not be prepared. Try again below; your saved scope and price will be reused."
            : "The authorization has not finished preparing. Continue below to recover it and review the recipient. It has not been sent."}
        </Alert>
      )}
      {error && <Alert color="danger">{error}</Alert>}
      {message && <Alert>{message}</Alert>}
      <Stack direction="row" gap={1} flexWrap="wrap">
        {!doc && !deal.readiness.ready && (
          <Button
            onClick={() =>
              onTab(
                deal.repair_items.length && !deal.terms.trim()
                  ? "details"
                  : "work",
              )
            }
          >
            {deal.repair_items.length && !deal.terms.trim()
              ? "Add repair terms"
              : "Complete repair scope"}
          </Button>
        )}
        {canReview && (
          <Button
            size="lg"
            loading={busy}
            disabled={pending}
            startDecorator={<Send size={18} />}
            onClick={() => void openSend()}
          >
            Review & send to customer
          </Button>
        )}
        {sent && doc?.contract_url && (
          <Button component={Link} to={doc.contract_url}>
            Check signing status / send reminder
          </Button>
        )}
        {doc?.pdf_url && (
          <Button variant="outlined" onClick={() => setPreview(true)}>
            Preview authorization
          </Button>
        )}
        {status === "draft" && (
          <Button
            variant="outlined"
            component={Link}
            to={`/crm/contracts/${doc!.contract_id}/sign-in-person`}
          >
            Sign in person
          </Button>
        )}
        {doc && !sent && !canReview && (
          <Button onClick={() => onTab("documents")}>
            Review authorization issue
          </Button>
        )}
      </Stack>
      {canReview && (
        <Typography
          level="body-sm"
          endDecorator={
            <HelpTooltip
              title="Sending a repair proposal"
              content="We prepare the saved scope, price, and terms as a repair authorization. You review the recipient and message before confirming delivery. Preparing or recovering the document does not send an email or create a job."
            />
          }
        >
          Nothing is sent until you confirm. After signing, an Org Admin
          confirms job setup.
        </Typography>
      )}
      <Divider />
      <Stack gap={1} alignItems="start">
        <Typography level="body-sm">
          Already approved or completed on site? Record the authorization here,
          then continue to job setup.
        </Typography>
        <ManualRepairAuthorization
          deal={deal}
          pending={pending || busy}
          refresh={refresh}
        />
      </Stack>
      {!doc &&
        !deal.readiness.ready &&
        deal.readiness.issues.map((issue) => (
          <Typography level="body-sm" key={issue}>
            {issue}
          </Typography>
        ))}
      {contract && (
        <SendContractModal
          open
          purpose="repair"
          onClose={() => setContract(null)}
          contract={contract}
          onSend={send}
        />
      )}
      {preview && doc?.pdf_url && (
        <PdfViewerModal
          open
          onClose={() => setPreview(false)}
          pdfUrl={doc.pdf_url}
          title="Repair authorization"
        />
      )}
    </Card>
  );
}
